/**
 * Skill bank for the mock first-round interview.
 * One entry per skill, 1 or 2 questions each, same shape and rules as packs.ts.
 * `{company}` is replaced at render time. Questions stay correct for any firm
 * in the skill's world.
 */

import type { SkillQuestion } from './packs';

export type SkillId =
  | 'accounting'
  | 'valuation'
  | 'financial-modeling'
  | 'markets'
  | 'case-structure'
  | 'market-sizing'
  | 'quant-reasoning'
  | 'excel'
  | 'marketing-strategy'
  | 'consumer-insight'
  | 'marketing-analytics'
  | 'sql-data'
  | 'product-metrics'
  | 'experimentation'
  | 'statistics'
  | 'coding-fundamentals'
  | 'ml-fundamentals'
  | 'audit'
  | 'tax'
  | 'supply-chain'
  | 'operations'
  | 'product-sense'
  | 'research'
  | 'communication';

export interface BankEntry {
  id: SkillId;
  /** Short round label, e.g. 'Accounting'. */
  label: string;
  questions: SkillQuestion[];
  /** What it tests, 6 words or fewer. */
  scoring: string;
}

export const BANK: Record<SkillId, BankEntry> = {
  accounting: {
    id: 'accounting',
    label: 'Accounting',
    scoring: 'Linking the three statements',
    questions: [
      {
        skill: 'Accounting',
        prompt: 'Depreciation goes up by $10. Tax rate is 25%. What happens to net income?',
        options: ['Falls by $7.50', 'Falls by $10', 'Rises by $2.50', 'No change, it’s non-cash'],
        correct: 0,
        hint: 'Taxes go down too.',
        good: 'Right.',
        bad: 'Careful.',
        why: 'Pre-tax income drops $10 and taxes drop $2.50. Net income falls $7.50.',
        fu: {
          prompt: 'And what happens to cash?',
          options: ['Falls by $7.50', 'No change', 'Rises by $2.50'],
          correct: 2,
          good: 'That’s the full walk.',
          bad: 'Depreciation is non-cash. Add it back.',
          why: 'Then add back $10 of non-cash depreciation and cash is up $2.50.',
        },
        noteGood: 'Walked the statements cleanly, including cash.',
        noteFu: 'Got net income. Missed the cash add-back.',
        noteHint: 'Forgot taxes at first, recovered.',
        noteMiss: 'Three-statement links need work.',
      },
      {
        skill: 'Accounting',
        prompt: 'A company buys $100 of inventory with cash. What happens to total assets?',
        options: ['Up $100', 'No change', 'Down $100', 'Up $100, and equity up $100'],
        correct: 1,
        hint: 'Two asset lines move. Which way each?',
        good: 'Good. One asset swapped for another.',
        bad: 'Check both sides of the trade.',
        why: 'Cash falls $100 and inventory rises $100. Total assets are unchanged and nothing hits the income statement yet.',
        fu: {
          prompt: 'It then sells that inventory for $150 cash. Ignoring taxes, what happens to equity?',
          options: ['Up $150', 'Up $50', 'No change'],
          correct: 1,
          good: 'Exactly. Profit lands in retained earnings.',
          bad: 'Equity moves by profit, not by sales.',
          why: 'Revenue $150 minus cost of goods $100 is $50 of profit. Retained earnings rise $50, matching the $50 net rise in assets.',
        },
        noteGood: 'Kept the balance sheet balanced at every step.',
        noteFu: 'Got the swap. Confused revenue with profit.',
        noteHint: 'Needed a nudge to see both asset lines.',
        noteMiss: 'Balance sheet mechanics need reps.',
      },
    ],
  },

  valuation: {
    id: 'valuation',
    label: 'Valuation',
    scoring: 'Methods and enterprise value',
    questions: [
      {
        skill: 'Valuation',
        prompt: 'Which method usually gives the highest valuation?',
        options: ['Trading comparables', 'Book value', 'Precedent transactions', 'The 52-week range'],
        correct: 2,
        hint: 'Think about what a buyer pays that a stock trader doesn’t.',
        good: 'Good.',
        bad: 'Hm, not quite.',
        why: 'Precedents include a control premium, the extra a buyer pays to take over the whole company.',
        fu: {
          prompt: 'Why do precedents come out higher?',
          options: ['They use older data', 'Buyers pay a control premium', 'They ignore debt'],
          correct: 1,
          good: 'That’s the one-sentence answer I want.',
          bad: 'It’s about paying for control.',
          why: 'A buyer pays extra for control. That premium is baked into precedent prices.',
        },
        noteGood: 'Knew the method and the reason behind it.',
        noteFu: 'Knew the answer, not the why.',
        noteHint: 'Got there with a nudge.',
        noteMiss: 'Valuation intuition needs reps.',
      },
      {
        skill: 'Valuation',
        prompt: 'Equity value is $500M. The company has $200M of debt and $50M of cash. What’s enterprise value?',
        options: ['$650M', '$750M', '$450M', '$550M'],
        correct: 0,
        hint: 'Add what lenders are owed. Then cash?',
        good: 'Right, and fast.',
        bad: 'Close. Watch the sign on cash.',
        why: 'Enterprise value is equity value plus debt minus cash: $500M plus $200M minus $50M = $650M.',
        fu: {
          prompt: 'Which multiple pairs with enterprise value?',
          options: ['Price / earnings', 'EV / EBITDA', 'Price / book'],
          correct: 1,
          good: 'Yes. Match the metric to who owns it.',
          bad: 'That one belongs to equity holders only.',
          why: 'EBITDA is earned before paying lenders or shareholders. It pairs with EV, which counts both.',
        },
        noteGood: 'Bridged equity to EV and matched the multiple.',
        noteFu: 'Got EV. Mismatched the multiple.',
        noteHint: 'Needed a nudge on the cash sign.',
        noteMiss: 'EV versus equity value is shaky.',
      },
    ],
  },

  'financial-modeling': {
    id: 'financial-modeling',
    label: 'Modeling',
    scoring: 'Projections and statement links',
    questions: [
      {
        skill: 'Modeling',
        prompt: 'Revenue is $200M and grows 10% a year. What’s revenue two years out?',
        options: ['$240M', '$242M', '$220M', '$400M'],
        correct: 1,
        hint: 'Year two grows on year one, not the start.',
        good: 'Good. You compounded it.',
        bad: 'Growth compounds. Build year by year.',
        why: '$200M × 1.1 = $220M, then $220M × 1.1 = $242M. Growth compounds; don’t just add 10% twice.',
        fu: {
          prompt: 'Where does net income land on the balance sheet?',
          options: ['Directly in cash', 'Retained earnings, in equity', 'Long-term debt'],
          correct: 1,
          good: 'Right. That’s the core link.',
          bad: 'Cash changes flow through the cash flow statement.',
          why: 'Net income less dividends adds to retained earnings. Cash only moves through the cash flow statement.',
        },
        noteGood: 'Compounded correctly and knew the key link.',
        noteFu: 'Math was right. Statement links fuzzy.',
        noteHint: 'Added growth instead of compounding at first.',
        noteMiss: 'Projection basics need work.',
      },
    ],
  },

  markets: {
    id: 'markets',
    label: 'Markets',
    scoring: 'Rates and bond prices',
    questions: [
      {
        skill: 'Markets',
        prompt: 'Interest rates rise. What happens to prices of existing bonds?',
        options: ['They rise', 'No change until they mature', 'They fall', 'Only short-term bonds move'],
        correct: 2,
        hint: 'New bonds now pay more. Who wants the old ones?',
        good: 'Right.',
        bad: 'Think about the new bonds on offer.',
        why: 'New bonds pay a higher coupon. An older, lower-coupon bond only sells at a lower price.',
        fu: {
          prompt: 'Which falls more: a 2-year bond or a 30-year bond?',
          options: ['The 2-year', 'The 30-year', 'They fall the same'],
          correct: 1,
          good: 'Yes. Duration is the word to use.',
          bad: 'Longer cash flows feel the rate more.',
          why: 'The 30-year’s cash flows are discounted over more years. Longer duration means a bigger price move.',
        },
        noteGood: 'Knew the direction and why duration matters.',
        noteFu: 'Got direction. Missed the maturity effect.',
        noteHint: 'Needed a nudge on the rate link.',
        noteMiss: 'Rates and prices link needs reps.',
      },
    ],
  },

  'case-structure': {
    id: 'case-structure',
    label: 'Structure',
    scoring: 'Breaking problems down first',
    questions: [
      {
        skill: 'Structure',
        prompt: 'Say you’re on a {company} team. A retail client’s profits fell 20% last year. Where do you start?',
        options: [
          'Cut costs across the board',
          'Look at what competitors are doing',
          'Split profit into revenue and costs, then find which moved',
          'Spend more on marketing',
        ],
        correct: 2,
        hint: 'What is profit made of?',
        good: 'Good. That’s exactly how I’d want you to open.',
        bad: 'I’d push back. You jumped to a fix before you knew what broke.',
        why: 'Structure before solutions. Profit is revenue minus costs, and interviewers score whether you break the problem down before guessing a fix.',
        fu: {
          prompt: 'Say revenue held flat but costs jumped. What next?',
          options: [
            'Raise prices to cover it',
            'Find which costs rose, and whether it’s one-off or ongoing',
            'Cut headcount',
          ],
          correct: 1,
          good: 'Exactly. One level deeper before recommending anything.',
          bad: 'That’s a fix again. Find the driver first.',
          why: 'Go one level deeper each time: costs, then which costs, then why.',
        },
        noteGood: 'Structured from the first sentence. Went deeper when pushed.',
        noteFu: 'Strong open. Lost the thread on the follow-up.',
        noteHint: 'Needed a nudge to structure, then got there.',
        noteMiss: 'Jumped to fixes before diagnosing.',
      },
      {
        skill: 'Structure',
        prompt:
          'Say you’re on a {company} team. A client asks whether to enter a new country. What do you look at first?',
        options: [
          'Whether the market is attractive: size, growth, margins',
          'Which city to open the first office in',
          'How to price the product there',
          'Which ad agency to hire locally',
        ],
        correct: 0,
        hint: 'Is it worth entering at all?',
        good: 'Good. Worth it before how.',
        bad: 'That’s execution. Answer whether first.',
        why: 'Market entry runs attractive market, then competition and ability to win, then how to enter. Execution questions come last.',
        fu: {
          prompt: 'The market is big and growing. What’s the next question?',
          options: [
            'Who competes there, and can the client win',
            'What the local logo should look like',
            'How fast to hire a sales team',
          ],
          correct: 0,
          good: 'Exactly. Big markets attract rivals.',
          bad: 'Still execution. Can they win first?',
          why: 'An attractive market is only worth it if the client can beat the incumbents on cost, brand, or capability.',
        },
        noteGood: 'Clean entry framework, in the right order.',
        noteFu: 'Good open. Skipped the competition step.',
        noteHint: 'Started on execution, then reframed.',
        noteMiss: 'Jumped to how before whether.',
      },
    ],
  },

  'market-sizing': {
    id: 'market-sizing',
    label: 'Market sizing',
    scoring: 'Top-down estimates, out loud',
    questions: [
      {
        skill: 'Market sizing',
        prompt:
          'A city has 2M people, about 2.5 per household. If 1 in 5 households buys a TV each year, how many TVs a year?',
        options: ['400K', '160K', '80K', '1.6M'],
        correct: 1,
        hint: 'Get to households first.',
        good: 'Nice. Clean steps.',
        bad: 'Slow down and go unit by unit.',
        why: '2M ÷ 2.5 = 800K households, and 800K ÷ 5 = 160K TVs a year. Say each step and its assumption out loud.',
        fu: {
          prompt: 'The average TV costs $500. What’s the yearly market?',
          options: ['$80M', '$800M', '$8B'],
          correct: 0,
          good: 'Good. Units times price.',
          bad: 'Count the zeros carefully.',
          why: '160K × $500 = $80M. Build on the number you already have.',
        },
        noteGood: 'Clear chain of assumptions, clean math.',
        noteFu: 'Good units. Slipped on the dollar step.',
        noteHint: 'Needed a nudge to start from households.',
        noteMiss: 'Estimation structure needs reps.',
      },
    ],
  },

  'quant-reasoning': {
    id: 'quant-reasoning',
    label: 'Numbers',
    scoring: 'Mental math under pressure',
    questions: [
      {
        skill: 'Numbers',
        prompt: 'A coffee chain has 200 stores, each selling 400 cups a day at $5. Rough annual revenue?',
        options: ['About $15M', 'About $400M', 'About $1.5B', 'About $146M'],
        correct: 3,
        hint: 'Start with total cups per day across all stores.',
        good: 'Nice, and quick.',
        bad: 'Not quite. Let’s slow down.',
        why: '200 × 400 = 80,000 cups. × $5 = $400K a day. × 365 ≈ $146M. Say the steps out loud; they matter more than the exact number.',
        fu: {
          prompt: 'Now 10% of stores close. Roughly what’s revenue?',
          options: ['Still about $146M', 'About $100M', 'About $131M'],
          correct: 2,
          good: 'Good. You adjusted instead of starting over.',
          bad: 'Adjust the number you already have.',
          why: '$146M × 0.9 ≈ $131M. Build on your last answer.',
        },
        noteGood: 'Comfortable with the math and adjusted on the fly.',
        noteFu: 'Got the estimate. Stumbled on the adjustment.',
        noteHint: 'Got the estimate with a hint.',
        noteMiss: 'Math under pressure needs reps.',
      },
      {
        skill: 'Numbers',
        prompt: 'A price rises 25%, then falls 20%. Where does it end up versus the start?',
        options: ['Back where it started', 'Up 5%', 'Down 5%', 'Down 1%'],
        correct: 0,
        hint: 'Try it on $100.',
        good: 'Right. Percentages don’t just add.',
        bad: 'Percent changes multiply. Try $100.',
        why: '$100 × 1.25 = $125, and $125 × 0.8 = $100. The 20% drop is taken off a bigger base.',
        fu: {
          prompt: 'A stock falls 50%. What gain gets it back to even?',
          options: ['50%', '100%', '150%'],
          correct: 1,
          good: 'Yes. Same trap, other direction.',
          bad: 'The base changed. Try $100 again.',
          why: '$100 falls to $50. Getting back to $100 takes a $50 gain on $50, which is 100%.',
        },
        noteGood: 'Treated percentages as multipliers, both ways.',
        noteFu: 'Got the first. Fell for the recovery trap.',
        noteHint: 'Needed a concrete number to anchor.',
        noteMiss: 'Adds percentages instead of multiplying.',
      },
    ],
  },

  excel: {
    id: 'excel',
    label: 'Excel',
    scoring: 'Lookups and cell references',
    questions: [
      {
        skill: 'Excel',
        prompt: 'You need to pull each product’s price from another table by its ID. Which function?',
        options: ['SUMIF', 'XLOOKUP or VLOOKUP', 'COUNTIF', 'CONCAT'],
        correct: 1,
        hint: 'You’re finding a match, not adding.',
        good: 'Right.',
        bad: 'That one aggregates. You need a match.',
        why: 'Lookups return a value from the row that matches a key. XLOOKUP also handles columns to the left of the key.',
        fu: {
          prompt: 'You copy =A1*$B$1 down one row. What does it become?',
          options: ['=A2*$B$2', '=A2*$B$1', '=A1*$B$1'],
          correct: 1,
          good: 'Exactly. Dollar signs lock the reference.',
          bad: 'The $ signs lock row and column.',
          why: 'A1 is relative and shifts to A2. $B$1 is absolute and stays put.',
        },
        noteGood: 'Knew lookups and absolute references cold.',
        noteFu: 'Knew the function. Shaky on references.',
        noteHint: 'Reached for an aggregate first.',
        noteMiss: 'Spreadsheet basics need reps.',
      },
    ],
  },

  'marketing-strategy': {
    id: 'marketing-strategy',
    label: 'Marketing case',
    scoring: 'Audience and funnel before tactics',
    questions: [
      {
        skill: 'Marketing case',
        prompt: '{company} wants Gen Z customers to try a new product line. What’s your first move?',
        options: [
          'Launch a TikTok campaign now',
          'Cut the price 20%',
          'Sign a famous creator or athlete',
          'Figure out who the customer is and what stops them trying it',
        ],
        correct: 3,
        hint: 'Before choosing a channel, who are you talking to?',
        good: 'Love that. Most people jump straight to the channel.',
        bad: 'That’s a tactic, not a first move.',
        why: 'Who and why before where. Find the audience and the insight before picking a channel.',
        fu: {
          prompt: 'What’s the one thing you’d want to learn about those customers?',
          options: [
            'Their favorite social platform',
            'What stops them trying it today',
            'How much they spend in the category',
          ],
          correct: 1,
          good: 'Yes. The barrier is the insight.',
          bad: 'Useful later, but not the insight.',
          why: 'The barrier becomes the message. The channel comes after.',
        },
        noteGood: 'Started with the audience. Found the insight.',
        noteFu: 'Right instinct, fuzzy on the insight.',
        noteHint: 'Went to tactics first, then corrected.',
        noteMiss: 'Leads with tactics over audience.',
      },
      {
        skill: 'Marketing case',
        prompt: 'Sales are flat. Most people know the brand, but few who know it buy. Where’s the problem?',
        options: [
          'Awareness: spend more on reach',
          'Between knowing and buying: something blocks the choice',
          'Retention: repeat buyers are leaving',
          'The logo needs a refresh',
        ],
        correct: 1,
        hint: 'Which funnel step is actually leaking?',
        good: 'Good. You found the leaky step.',
        bad: 'Awareness is already high. Look further down.',
        why: 'Diagnose the funnel before spending. High awareness with low purchase points to consideration or conversion, not reach.',
        fu: {
          prompt: 'What would you look at first?',
          options: [
            'Why people who know the brand pick a competitor',
            'Total ad impressions last quarter',
            'Follower counts on each platform',
          ],
          correct: 0,
          good: 'Yes. The lost choice is the insight.',
          bad: 'That measures reach, which isn’t the problem.',
          why: 'The reason people choose a rival tells you what to fix: price, product, or message.',
        },
        noteGood: 'Diagnosed the funnel and went for the why.',
        noteFu: 'Found the step. Reached for vanity metrics after.',
        noteHint: 'Wanted more reach first, then reframed.',
        noteMiss: 'Spends before diagnosing the funnel.',
      },
    ],
  },

  'consumer-insight': {
    id: 'consumer-insight',
    label: 'Consumer insight',
    scoring: 'What people do versus say',
    questions: [
      {
        skill: 'Consumer insight',
        prompt:
          'In a survey, 70% of customers wanted a cheaper option. The cheaper version barely sold. Best explanation to test?',
        options: [
          'Surveys are always useless',
          'The price wasn’t cut enough',
          'What people say differs from what they do at the shelf',
          'Customers answered at random',
        ],
        correct: 2,
        hint: 'Is saying the same as buying?',
        good: 'Good. That gap is the classic insight trap.',
        bad: 'Possible, but look at the survey itself.',
        why: 'Stated preference often differs from real behavior. Strong insight work checks what people actually do.',
        fu: {
          prompt: 'What’s the best next research step?',
          options: [
            'Run a bigger version of the same survey',
            'Watch or interview recent buyers about the moment they chose',
            'Ask the sales team what they think',
          ],
          correct: 1,
          good: 'Yes. Get close to the real decision.',
          bad: 'That repeats the same blind spot.',
          why: 'Observing or interviewing real buyers shows the trade-offs they made, which a survey question can’t.',
        },
        noteGood: 'Separated stated from actual behavior.',
        noteFu: 'Spotted the gap. Weak on the next method.',
        noteHint: 'Blamed price first, then saw the gap.',
        noteMiss: 'Takes survey answers at face value.',
      },
    ],
  },

  'marketing-analytics': {
    id: 'marketing-analytics',
    label: 'Reading numbers',
    scoring: 'Rates over raw totals',
    questions: [
      {
        skill: 'Reading numbers',
        prompt: 'Campaign A: 2M views, 1,000 purchases. B: 200K views, 800 purchases. Which converted better?',
        options: ['B, about 8x the conversion rate', 'A, 10x the reach', 'About the same', 'Can’t compare them'],
        correct: 0,
        hint: 'Divide purchases by views for each.',
        good: 'Exactly. Reach is the vanity number.',
        bad: 'Check the rate, not the total.',
        why: '1,000 ÷ 2M = 0.05%. 800 ÷ 200K = 0.4%. Rates beat raw totals.',
        fu: {
          prompt: 'Before calling B the winner, what else would you check?',
          options: ['Which got more likes', 'Which ran longer', 'Cost per purchase for each'],
          correct: 2,
          good: 'Exactly. That decides where the next dollar goes.',
          bad: 'That’s another vanity number.',
          why: 'Cost per purchase decides what to fund next.',
        },
        noteGood: 'Looked past reach to conversion and cost.',
        noteFu: 'Read the rate, missed cost.',
        noteHint: 'Distracted by reach at first.',
        noteMiss: 'Reads totals, not rates.',
      },
    ],
  },

  'sql-data': {
    id: 'sql-data',
    label: 'SQL',
    scoring: 'Joins, filters, and NULLs',
    questions: [
      {
        skill: 'SQL',
        prompt: 'You need every customer, including ones who never ordered. Which join?',
        options: [
          'customers INNER JOIN orders',
          'orders LEFT JOIN customers',
          'UNION the two tables',
          'customers LEFT JOIN orders',
        ],
        correct: 3,
        hint: 'Which table must keep all its rows?',
        good: 'Right, and the table order matters.',
        bad: 'That drops or flips the rows you need.',
        why: 'A LEFT JOIN keeps every row from the left table. Customers go on the left.',
        fu: {
          prompt: 'For customers with no orders, what shows in the order columns?',
          options: ['NULL', '0', 'An empty string'],
          correct: 0,
          good: 'Yes. And NULL trips up counts and sums.',
          bad: 'No match means no value at all.',
          why: 'Unmatched rows get NULL. Handle it before counting or averaging.',
        },
        noteGood: 'Picked the right join and knew about NULLs.',
        noteFu: 'Right join. Unsure what unmatched rows hold.',
        noteHint: 'Needed a nudge on which side to keep.',
        noteMiss: 'Join types need reps.',
      },
      {
        skill: 'SQL',
        prompt: 'You want customers with more than 5 orders. Which clause filters on that count?',
        options: ['WHERE', 'HAVING', 'ORDER BY', 'LIMIT'],
        correct: 1,
        hint: 'Is the filter before or after grouping?',
        good: 'Right.',
        bad: 'That runs before the rows are grouped.',
        why: 'WHERE filters rows before GROUP BY. HAVING filters groups after, which is where aggregates like COUNT live.',
        fu: {
          prompt: 'What does COUNT(email) skip that COUNT(*) counts?',
          options: ['Duplicate emails', 'Nothing, they always match', 'Rows where email is NULL'],
          correct: 2,
          good: 'Exactly. A classic silent bug.',
          bad: 'Duplicates are counted by both.',
          why: 'COUNT(column) ignores NULLs. COUNT(*) counts every row.',
        },
        noteGood: 'Knew WHERE versus HAVING and NULL counting.',
        noteFu: 'Got HAVING. Missed the NULL behavior.',
        noteHint: 'Reached for WHERE first, then corrected.',
        noteMiss: 'Aggregation order needs work.',
      },
    ],
  },

  'product-metrics': {
    id: 'product-metrics',
    label: 'Diagnosis',
    scoring: 'Diagnosing and choosing metrics',
    questions: [
      {
        skill: 'Diagnosis',
        prompt: 'Say you’re on a {company} team. Daily active users fell 10% overnight. What’s your first move?',
        options: [
          'Tell the team to roll back the last release',
          'Launch a re-engagement email campaign',
          'Check it’s not a tracking bug, then segment by platform, region, and version',
          'Wait a week to see if it recovers',
        ],
        correct: 2,
        hint: 'Is the drop real? And where is it?',
        good: 'Good. Confirm it’s real, then find where it lives.',
        bad: 'That’s a fix. You don’t know what broke yet.',
        why: 'A sudden overnight drop is often a logging or pipeline issue. Rule that out, then segment to see where the drop is concentrated.',
        fu: {
          prompt: 'Tracking checks out. The drop is almost all on Android. What next?',
          options: [
            'Assume it’s seasonality',
            'Survey all users about the app',
            'Check what changed on Android that day, like a new app version',
          ],
          correct: 2,
          good: 'Exactly. Tie the drop to a change.',
          bad: 'Seasonality wouldn’t hit only one platform.',
          why: 'A drop on one platform points to something platform-specific. Look for a release, an OS update, or an app store issue on that date.',
        },
        noteGood: 'Checked data quality first, then segmented cleanly.',
        noteFu: 'Good first check. Didn’t link the drop to a change.',
        noteHint: 'Needed a nudge to question the data.',
        noteMiss: 'Jumped to fixes before confirming the drop.',
      },
      {
        skill: 'Diagnosis',
        prompt: 'Which metric best shows whether users get lasting value from a product?',
        options: [
          'Share of new users still active 4 weeks later',
          'Total downloads',
          'Total page views',
          'Features shipped this quarter',
        ],
        correct: 0,
        hint: 'Which one shows people coming back?',
        good: 'Right. Retention is the honest one.',
        bad: 'That can grow while users leave.',
        why: 'Downloads and views count activity once. Retention shows whether people found enough value to return.',
        fu: {
          prompt: 'Daily actives are 200K and monthly actives are 1M. What does that tell you?',
          options: [
            '5x: users are very sticky',
            '80%: most users come daily',
            '20%: users are active about 6 days a month on average',
          ],
          correct: 2,
          good: 'Yes. DAU/MAU, read in plain English.',
          bad: 'Divide daily by monthly, not the reverse.',
          why: '200K ÷ 1M = 20%. Across a 30-day month that’s about 6 active days per monthly user.',
        },
        noteGood: 'Chose retention and read DAU/MAU correctly.',
        noteFu: 'Right metric. Fumbled the stickiness ratio.',
        noteHint: 'Drawn to volume metrics at first.',
        noteMiss: 'Picks vanity metrics over retention.',
      },
    ],
  },

  experimentation: {
    id: 'experimentation',
    label: 'A/B testing',
    scoring: 'Lift and significance',
    questions: [
      {
        skill: 'A/B testing',
        prompt:
          'Checkout test. Control: 400 purchases from 10,000 visitors. New design: 460 from 10,000. What’s the lift?',
        options: ['6% relative lift', '15% relative lift', '60% relative lift', 'Can’t tell without revenue'],
        correct: 1,
        hint: 'Get each conversion rate first.',
        good: 'Right. Rates first, then the change.',
        bad: 'Close. Compute both rates before comparing.',
        why: '400 ÷ 10,000 = 4.0%. 460 ÷ 10,000 = 4.6%. That’s 0.6 points, and 0.6 ÷ 4.0 = a 15% relative lift. Say which one you mean.',
        fu: {
          prompt: 'Before you ship it, what would you check?',
          options: [
            'Whether the difference is statistically significant',
            'Which version the team likes better',
            'Whether the new design looks more modern',
          ],
          correct: 0,
          good: 'Yes. A lift you can’t trust isn’t a lift.',
          bad: 'Opinions don’t settle a test.',
          why: 'Check significance and that the test ran long enough. Here z is about 2.1, which clears the usual 5% bar, but only just.',
        },
        noteGood: 'Got the rates, the lift, and asked about significance.',
        noteFu: 'Got the lift. Didn’t question whether it’s real.',
        noteHint: 'Needed a nudge to compare rates.',
        noteMiss: 'Mixes up absolute and relative change.',
      },
    ],
  },

  statistics: {
    id: 'statistics',
    label: 'Statistics',
    scoring: 'Skew and correlation versus cause',
    questions: [
      {
        skill: 'Statistics',
        prompt: 'Most orders are between $20 and $40, but a few are $5,000. Which number best shows a typical order?',
        options: ['The mean', 'The maximum', 'Total revenue divided by customers', 'The median'],
        correct: 3,
        hint: 'Which one ignores a few huge values?',
        good: 'Right. Outliers drag the mean.',
        bad: 'Those big orders pull that number up.',
        why: 'With skewed data the median shows the typical case. A few huge orders inflate the mean.',
        fu: {
          prompt: 'Users of feature X retain better. Can you say the feature causes it?',
          options: [
            'Yes, the gap is large',
            'Yes, if the sample is big enough',
            'Not yet. Engaged users may just find it more',
          ],
          correct: 2,
          good: 'Exactly. Correlation, not cause yet.',
          bad: 'Sample size doesn’t fix selection.',
          why: 'Engaged users self-select into features. A randomized test is how you show cause.',
        },
        noteGood: 'Handled skew and spotted the selection trap.',
        noteFu: 'Knew the median. Took correlation as cause.',
        noteHint: 'Reached for the mean first.',
        noteMiss: 'Summary stats and causality need reps.',
      },
    ],
  },

  'coding-fundamentals': {
    id: 'coding-fundamentals',
    label: 'Coding',
    scoring: 'Data structures and Big-O',
    questions: [
      {
        skill: 'Coding',
        prompt:
          'You check whether a username is taken millions of times a day. Which structure gives the fastest lookups?',
        options: ['A sorted array', 'A linked list', 'A hash set', 'A stack'],
        correct: 2,
        hint: 'Which one jumps straight to the item?',
        good: 'Right.',
        bad: 'That one has to search.',
        why: 'A hash set finds an item in constant time on average. A sorted array needs binary search; a list or stack scans.',
        fu: {
          prompt: 'What’s the average lookup time in a hash set?',
          options: ['O(1)', 'O(log n)', 'O(n)'],
          correct: 0,
          good: 'Yes. Constant on average.',
          bad: 'That’s binary search or a scan.',
          why: 'Hashing jumps to the bucket directly: O(1) on average. O(log n) is binary search on a sorted array.',
        },
        noteGood: 'Picked the structure and knew its cost.',
        noteFu: 'Right structure. Fuzzy on the Big-O.',
        noteHint: 'Needed a nudge toward hashing.',
        noteMiss: 'Data structure trade-offs need reps.',
      },
      {
        skill: 'Coding',
        prompt:
          'A loop runs over a list of n items, and inside it another loop runs over the same list. What’s the Big-O?',
        options: ['O(n)', 'O(n²)', 'O(log n)', 'O(2n)'],
        correct: 1,
        hint: 'How many times does the inner line run?',
        good: 'Right.',
        bad: 'Nested loops multiply, they don’t add.',
        why: 'The inner loop runs n times for each of n outer passes: n × n = O(n²).',
        fu: {
          prompt: 'In Python, what does len([1, [2, 3], 4]) return?',
          options: ['4', '2', '3'],
          correct: 2,
          good: 'Yes. The inner list is one item.',
          bad: 'Count top-level items only.',
          why: 'The list holds three items: 1, the list [2, 3], and 4. len counts top-level items.',
        },
        noteGood: 'Read the complexity and the snippet correctly.',
        noteFu: 'Got Big-O. Tripped on the nested list.',
        noteHint: 'Added the loops before multiplying.',
        noteMiss: 'Complexity basics need work.',
      },
    ],
  },

  'ml-fundamentals': {
    id: 'ml-fundamentals',
    label: 'Machine learning',
    scoring: 'Overfitting and precision',
    questions: [
      {
        skill: 'Machine learning',
        prompt: 'Your model scores 99% on training data and 70% on test data. What’s most likely happening?',
        options: [
          'Overfitting: it memorized the training data',
          'Underfitting: the model is too simple',
          'The test set is too small to matter',
          'It needs more training epochs',
        ],
        correct: 0,
        hint: 'Great on data it saw, weak on new data.',
        good: 'Right.',
        bad: 'An underfit model is weak on both.',
        why: 'A big gap between train and test scores is the signature of overfitting. The model learned noise, not the pattern.',
        fu: {
          prompt: 'A fraud model flags 100 transactions and 20 are real fraud. What’s its precision?',
          options: ['Can’t tell without total fraud cases', '80%', '20%'],
          correct: 2,
          good: 'Yes. Of what it flagged, how many were right.',
          bad: 'Total fraud cases is what recall needs.',
          why: 'Precision is true positives over everything flagged: 20 ÷ 100 = 20%. Recall would need the total fraud count.',
        },
        noteGood: 'Spotted overfitting and computed precision cleanly.',
        noteFu: 'Knew overfitting. Mixed up precision and recall.',
        noteHint: 'Needed a nudge on the train-test gap.',
        noteMiss: 'Core ML evaluation needs reps.',
      },
    ],
  },

  audit: {
    id: 'audit',
    label: 'Audit',
    scoring: 'Existence versus completeness testing',
    questions: [
      {
        skill: 'Audit',
        prompt: 'An auditor wants to confirm that recorded sales really happened. Which way do they test?',
        options: [
          'Start from shipping documents and trace into the books',
          'Start from recorded sales and vouch back to invoices and shipping documents',
          'Recalculate total revenue in a spreadsheet',
          'Ask management to confirm the total',
        ],
        correct: 1,
        hint: 'Start from what’s in the books.',
        good: 'Right. That tests existence.',
        bad: 'That direction tests something else.',
        why: 'To test that recorded items exist, start with the books and vouch back to evidence. Tracing the other way tests completeness.',
        fu: {
          prompt: 'Now you want to know no sales were left out. Which direction?',
          options: [
            'From shipping documents forward into the books',
            'From the books back to the documents',
            'Compare this year’s total to last year’s',
          ],
          correct: 0,
          good: 'Yes. Completeness runs the other way.',
          bad: 'That can’t find what’s missing from the books.',
          why: 'Missing sales aren’t in the books to start from. Begin with source documents and trace forward.',
        },
        noteGood: 'Knew both test directions and why.',
        noteFu: 'Got existence. Missed completeness direction.',
        noteHint: 'Needed a nudge on where to start.',
        noteMiss: 'Assertion testing basics need work.',
      },
    ],
  },

  tax: {
    id: 'tax',
    label: 'Tax',
    scoring: 'Deferred taxes and effective rates',
    questions: [
      {
        skill: 'Tax',
        prompt: 'Which of these typically creates a deferred tax asset?',
        options: [
          'Accelerated depreciation on the tax return',
          'Paying a cash dividend',
          'A tax loss carried forward to future years',
          'Buying inventory on credit',
        ],
        correct: 2,
        hint: 'Which one lowers future tax bills?',
        good: 'Right.',
        bad: 'That one creates a liability, or nothing.',
        why: 'A loss carryforward can reduce future taxable income, which makes it an asset. Accelerated tax depreciation creates a deferred tax liability.',
        fu: {
          prompt:
            'Say someone’s last dollar is taxed at 22%, and they owe $8,000 on $60,000 of taxable income. Effective rate?',
          options: ['About 13%', '22%', 'About 35%'],
          correct: 0,
          good: 'Yes. Effective is total tax over income.',
          bad: '22% is the marginal rate, not effective.',
          why: '$8,000 ÷ $60,000 ≈ 13.3%. Brackets are progressive, making the effective rate lower than the marginal rate.',
        },
        noteGood: 'Knew deferred taxes and marginal versus effective.',
        noteFu: 'Got the DTA. Confused marginal and effective.',
        noteHint: 'Needed a nudge on future tax savings.',
        noteMiss: 'Tax basics need reps.',
      },
    ],
  },

  'supply-chain': {
    id: 'supply-chain',
    label: 'Supply chain',
    scoring: 'Inventory turns and safety stock',
    questions: [
      {
        skill: 'Supply chain',
        prompt: 'Cost of goods sold is $12M a year and average inventory is $2M. How many inventory turns?',
        options: ['6', '24', '0.17', '10'],
        correct: 0,
        hint: 'Cost of goods divided by what?',
        good: 'Right. About two months of stock.',
        bad: 'Flip it. COGS over average inventory.',
        why: '$12M ÷ $2M = 6 turns a year. That’s about 365 ÷ 6 ≈ 61 days of inventory on hand.',
        fu: {
          prompt: 'Supplier delivery times start varying a lot more. What should go up?',
          options: ['Selling price', 'Safety stock', 'Shelf space for slow sellers'],
          correct: 1,
          good: 'Yes. Buffer the uncertainty.',
          bad: 'That doesn’t protect against stockouts.',
          why: 'Safety stock covers variability in demand and lead time. More variability needs a bigger buffer.',
        },
        noteGood: 'Computed turns and sized the buffer right.',
        noteFu: 'Got turns. Missed the safety stock link.',
        noteHint: 'Inverted the ratio at first.',
        noteMiss: 'Inventory basics need reps.',
      },
    ],
  },

  operations: {
    id: 'operations',
    label: 'Operations',
    scoring: 'Finding the bottleneck',
    questions: [
      {
        skill: 'Operations',
        prompt: 'A line has three steps: cutting does 60 units an hour, sewing 40, packing 80. Max output per hour?',
        options: ['60', '80', '40', '180'],
        correct: 2,
        hint: 'Every unit passes through every step.',
        good: 'Right. The slowest step sets the pace.',
        bad: 'Steps run in sequence, not in parallel.',
        why: 'Output is capped by the bottleneck. Sewing handles 40 an hour, whatever the other steps can do.',
        fu: {
          prompt: 'You can add capacity to one step. Which one?',
          options: ['Cutting', 'Sewing', 'Packing'],
          correct: 1,
          good: 'Exactly. Only the bottleneck moves output.',
          bad: 'That step isn’t what’s holding output back.',
          why: 'Adding capacity anywhere but the bottleneck leaves output unchanged. Raise sewing first.',
        },
        noteGood: 'Found the bottleneck and knew where to invest.',
        noteFu: 'Found it. Invested in the wrong step.',
        noteHint: 'Needed a nudge on sequential flow.',
        noteMiss: 'Bottleneck thinking needs work.',
      },
    ],
  },

  'product-sense': {
    id: 'product-sense',
    label: 'Product sense',
    scoring: 'User need before features',
    questions: [
      {
        skill: 'Product sense',
        prompt: 'Say you’re on a {company} team and asked to improve a product. Where do you start?',
        options: [
          'List every feature you’d add',
          'Pick a target user and find their biggest unmet need',
          'Copy what the top competitor just shipped',
          'Redesign the home screen',
        ],
        correct: 1,
        hint: 'Improve it for whom?',
        good: 'Good. User and problem first.',
        bad: 'That’s a solution before a problem.',
        why: 'Strong product answers pick a user, name their pain, then propose solutions. Features come last.',
        fu: {
          prompt: 'You have three ideas. How do you pick one?',
          options: [
            'Build the most exciting one',
            'Build all three at once',
            'Rank them by user impact against effort',
          ],
          correct: 2,
          good: 'Yes. Say the trade-off out loud.',
          bad: 'Interviewers want a clear prioritization call.',
          why: 'Prioritize by impact on the user’s problem versus cost to build, and name what you’d measure.',
        },
        noteGood: 'Started with the user and prioritized clearly.',
        noteFu: 'Good start. Weak prioritization call.',
        noteHint: 'Jumped to features, then reframed.',
        noteMiss: 'Leads with features over users.',
      },
    ],
  },

  research: {
    id: 'research',
    label: 'Research',
    scoring: 'Sourcing and checking evidence',
    questions: [
      {
        skill: 'Research',
        prompt: 'An article says a market grew 40% last year. What do you do before using it in a report?',
        options: [
          'Use it as-is, it’s published',
          'Average it with your own guess',
          'Cite the site that repeated it',
          'Find the original source and check its date and how it defines the market',
        ],
        correct: 3,
        hint: 'Where did the number come from?',
        good: 'Right. Trace it to the source.',
        bad: 'Published isn’t the same as verified.',
        why: 'Good research traces a number to its origin and checks the definition and date. A secondhand figure can be stale or mislabeled.',
        fu: {
          prompt: 'Two credible sources give different numbers. What do you write?',
          options: [
            'Show both, explain why they differ, and say which you rely on',
            'Pick the higher number',
            'Leave the number out',
          ],
          correct: 0,
          good: 'Yes. Transparent and still decisive.',
          bad: 'That hides the disagreement from the reader.',
          why: 'Readers trust research that shows its sources, explains the gap, and still makes a call.',
        },
        noteGood: 'Checked sources and handled conflicts openly.',
        noteFu: 'Checked sourcing. Weak on conflicting data.',
        noteHint: 'Trusted the article at first.',
        noteMiss: 'Uses numbers without checking them.',
      },
    ],
  },

  communication: {
    id: 'communication',
    label: 'Communication',
    scoring: 'Specific, evidence-backed answers',
    questions: [
      {
        skill: 'Communication',
        prompt: 'An interviewer asks, “Why {company}?” Which answer scores highest?',
        options: [
          '“You’re a top name and it would look great on my résumé.”',
          '“I really love your culture and values.”',
          '“I’m applying broadly and this seems like a great fit.”',
          '“A class project showed me I like this work. Someone on your team told me what they’re building, and I want to learn that.”',
        ],
        correct: 3,
        hint: 'Which one could only be said about this firm?',
        good: 'Yes. Specific and backed by something real.',
        bad: 'That could be said to any firm.',
        why: 'Interviewers score specifics: a real experience that drew you in and something concrete you learned about the team.',
        fu: {
          prompt: 'Next: “Tell me about yourself.” How should you open?',
          options: [
            'Where you grew up and your hobbies',
            'Your GPA and every class you’ve taken',
            'What you do now, one proof point, and why this role',
          ],
          correct: 2,
          good: 'Right. Short, relevant, and it leads somewhere.',
          bad: 'Relevant to the role beats complete.',
          why: 'A tight present, proof, and why-this-role arc in under a minute sets up the rest of the interview.',
        },
        noteGood: 'Specific, evidence-backed, and concise.',
        noteFu: 'Strong why-us. Rambling self-introduction.',
        noteHint: 'Went generic first, then got specific.',
        noteMiss: 'Answers are generic and could fit any firm.',
      },
    ],
  },
};
